import React from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import "dayjs/locale/en";
import {
  ItemWrapper,
  ContentWrapper,
  Error,
  InputWrapper,
  InputContainer,
  ButtonsWrapper,
  NotificationWrapper,
} from "../../styles";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { CNYProps, FormInputs } from "../../../../../../types/walletTypes";
import { savedCNYData } from "../../../../redux/formDataSlice";
import { Input } from "../../../../../../components/common/Input";
import CountrySelect from "../../../../../../components/common/CountrySelect";
import { Button } from "../../../../../../components/common/Button";
import { InputPhone } from "../../../../../../components/common/InputPhone";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import { CNYIDTypes } from "../../../../const";
import { ReactComponent as InfoIcon } from "../../../../../../assets/icons/info.svg";

const BusinessSenderForm = ({ handleNext, handlePrevious }: CNYProps) => {
  const getSavedData = useAppSelector((state) => state.formData.cnyData);

  const { ...defaultValues } = getSavedData || {};

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues,
  });

  const dispatch = useAppDispatch();

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    dispatch(savedCNYData(data));
    handleNext?.();
  };

  return (
    <ContentWrapper>
      <InputWrapper>
        <Controller
          name="receiverBusinessRegisteredName"
          control={control}
          rules={{ required: "Business name is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Registered business name"
              value={field.value}
              onChange={field.onChange}
              hasError={!!errors?.receiverBusinessRegisteredName}
            />
          )}
        />
        <NotificationWrapper>
          <InfoIcon />
          <p>The name must be in Chinese</p>
        </NotificationWrapper>
        {<Error>{errors?.receiverBusinessRegisteredName?.message}</Error>}
      </InputWrapper>

      <ItemWrapper>
        <InputContainer>
          <InputWrapper>
            <Controller
              name="receiverBusinessIdType"
              control={control}
              rules={{ required: "ID type is required" }}
              render={({ field }) => (
                <CustomSelect
                  value={field.value}
                  options={CNYIDTypes.filter(
                    (data) =>
                      ![
                        "BUSINESS_REGISTRATION_CERT",
                        "PASSPORT",
                        "OTHER",
                        "HONGKONG_MACAO_TAIWAN_PERMIT",
                        "ID_CARD",
                      ].includes(data.value),
                  )}
                  onChange={field.onChange}
                  $width="100%"
                  placeholder="ID Type"
                  lightBorder
                />
              )}
            />
            {<Error>{errors?.receiverBusinessIdType?.message}</Error>}
          </InputWrapper>
        </InputContainer>

        <InputWrapper>
          <Controller
            name="receiverBusinessIdNumber"
            control={control}
            rules={{ required: "Business ID number is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Business ID number"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.receiverBusinessIdNumber}
              />
            )}
          />
          {<Error>{errors?.receiverBusinessIdNumber?.message}</Error>}
        </InputWrapper>
      </ItemWrapper>
      <InputWrapper>
        <Controller
          name="receiverBusinessAddress.streetAddress"
          control={control}
          rules={{ required: "Street address is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Street address"
              value={field.value}
              onChange={field.onChange}
              hasError={!!errors?.receiverBusinessAddress?.streetAddress}
            />
          )}
        />
        {
          <Error>
            {errors?.receiverBusinessAddress?.streetAddress?.message}
          </Error>
        }
      </InputWrapper>

      <ItemWrapper>
        <InputWrapper>
          <Controller
            name="receiverBusinessAddress.city"
            control={control}
            rules={{ required: "City is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="City"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.receiverBusinessAddress?.city}
              />
            )}
          />
          {<Error>{errors?.receiverBusinessAddress?.city?.message}</Error>}
        </InputWrapper>

        <InputWrapper>
          <Controller
            name="receiverBusinessAddress.state"
            control={control}
            rules={{ required: "State is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="State"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.receiverBusinessAddress?.state}
              />
            )}
          />
          {<Error>{errors?.receiverBusinessAddress?.state?.message}</Error>}
        </InputWrapper>
      </ItemWrapper>

      <ItemWrapper>
        <InputContainer>
          <InputWrapper>
            <Controller
              name="receiverBusinessAddress.countryCode"
              control={control}
              rules={{ required: "Country is required" }}
              render={({ field }) => (
                <InputWrapper>
                  <CountrySelect
                    value={field.value}
                    onChange={field.onChange}
                    allCountries={true}
                    placeholder="Country"
                    lightBorder
                  />
                </InputWrapper>
              )}
            />
            {
              <Error>
                {errors?.receiverBusinessAddress?.countryCode?.message}
              </Error>
            }
          </InputWrapper>
        </InputContainer>

        <InputWrapper>
          <Controller
            name="receiverBusinessAddress.postcode"
            control={control}
            rules={{ required: "Postcode is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Postcode"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.receiverBusinessAddress?.postcode}
              />
            )}
          />
          {<Error>{errors?.receiverBusinessAddress?.postcode?.message}</Error>}
        </InputWrapper>
      </ItemWrapper>

      <InputWrapper>
        <Controller
          name="receiverMobileNumber"
          control={control}
          rules={{ required: "Phone number is required" }}
          render={({ field }) => (
            <InputPhone
              label="Phone number"
              field={field}
              defaultCountry="CN"
            />
          )}
        />
        <Error>{errors?.receiverMobileNumber?.message}</Error>
      </InputWrapper>

      <ButtonsWrapper>
        <Button
          onClick={handlePrevious}
          theme="alternate"
          height="48px"
          width="203px"
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          label="Continue"
          height="48px"
        />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default BusinessSenderForm;
